import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    BrowserStreamMessagesResponseMessages,
    BrowserStreamMessagesResponseMessagesTypeEnum,
} from '../../../../backendsdk/api';
import { TrackedDialog as Dialog } from '../../../../components/TrackedComponents';
import useApi from '../../../../hooks/api';
import { STREAMS_GRID_DEFS } from '../../Event/EventVideoStreams';
import {
    CAMERAS_NS,
    ConnectionStatus,
    LIVE_STREAM_NS,
    MAX_VIDEO_FEEDS,
    POLLING_INTERVAL_MS,
    TRANSLATABLE_CAMERA_NAMES,
    VideoFeedSlot,
    dataChannelPayloadToString,
    getErrorMessage,
    handleDataChannelMessage,
    parseTrackNamesFromSdp,
} from './Defs';
import LiveStreamExitConfirmDialog from './LiveStreamExitConfirmDialog';
import LiveStreamModalHeader from './LiveStreamModalHeader';
import LiveStreamVideoFeed from './LiveStreamVideoFeed';

interface LiveStreamModalProps {
    onClose: () => void;
    sessionId?: number;
    /** Vehicle identifier (e.g. license plate or VIN) to show in the header. */
    vin?: string;
    /** ICE servers parsed from live_stream command payload (browser_credentials). */
    iceServers?: RTCIceServer[];
}

const LiveStreamModal: React.FC<LiveStreamModalProps> = ({ onClose, sessionId, vin, iceServers }) => {
    const [videoFeeds, setVideoFeeds] = useState<VideoFeedSlot[]>([]);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    /** mid -> track_name map received from device over data channel (or parsed from SDP); used for display. */
    const [trackNameByMid, setTrackNameByMid] = useState<Record<string, string>>({});
    const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);
    /** trackId of the feed shown as main (large). When null, first feed is main. */
    const [primaryTrackId, setPrimaryTrackId] = useState<string | null>(null);
    /** When true, show confirmation dialog before ending the session. */
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    /** Set in cleanup (true) and connect (false); connection_ended is set in cleanup before closing so the connectionstatechange handler preserves it via functional update. */
    const [closedGracefully, setClosedGracefully] = useState<boolean>(false);
    const [lastMessageId, setLastMessageId] = useState<number>(-1);
    const [answerProcessed, setAnswerProcessed] = useState<boolean>(false);

    const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    /** ICE candidates received before the SDP answer is applied; drained after setRemoteDescription. */
    const pendingIceCandidatesRef = useRef<BrowserStreamMessagesResponseMessages[]>([]);

    const { t } = useTranslation();
    const { api } = useApi();

    const getStatusDisplayText = (status: ConnectionStatus, errMsg: string | null): string => {
        if (errMsg) return errMsg;
        const keyByStatus: Record<ConnectionStatus, string> = {
            connecting: `${LIVE_STREAM_NS}.status_connecting`,
            connected: `${LIVE_STREAM_NS}.status_streaming`,
            reconnecting: `${LIVE_STREAM_NS}.status_reconnecting`,
            disconnected: `${LIVE_STREAM_NS}.status_disconnected`,
            connection_ended: `${LIVE_STREAM_NS}.status_connection_ended`,
        };
        return t(keyByStatus[status] ?? `${LIVE_STREAM_NS}.status_disconnected`);
    };

    const getCameraDisplayName = (rawName: string): string => {
        if (TRANSLATABLE_CAMERA_NAMES.has(rawName)) {
            return t(`${CAMERAS_NS}.${rawName}`);
        }
        return rawName;
    };

    // Derive connection status from RTCPeerConnection.connectionState (single source of truth).
    useEffect(() => {
        if (!peerConnection) {
            return;
        }

        const deriveStatus = () => {
            const state = peerConnection.connectionState;
            const statusByState: Record<RTCPeerConnectionState, ConnectionStatus> = {
                closed: closedGracefully ? 'connection_ended' : 'disconnected',
                failed: 'disconnected',
                disconnected: 'reconnecting',
                connected: 'connected',
                new: 'connecting',
                connecting: 'connecting',
            };
            setConnectionStatus(statusByState[state] ?? 'disconnected');
            if (state === 'connected') {
                setErrorMessage(null);
            }
        };

        deriveStatus();
        peerConnection.addEventListener('connectionstatechange', deriveStatus);
        return () => {
            peerConnection.removeEventListener('connectionstatechange', deriveStatus);
        };
    }, [peerConnection, closedGracefully]);

    useEffect(() => {
        const activeTrackIds = videoFeeds.filter((f): f is VideoFeedSlot => f !== null).map((f) => f.trackId);
        if (primaryTrackId && !activeTrackIds.includes(primaryTrackId)) {
            setPrimaryTrackId(null);
        }
    }, [videoFeeds, primaryTrackId]);

    useEffect(() => {
        if (connectionStatus === 'disconnected' || connectionStatus === 'connection_ended') {
            return;
        }

        if (!sessionId) {
            return;
        }

        let cancelled = false;
        let timeoutId: number | undefined;

        const runPoll = async () => {
            if (cancelled) {
                return;
            }
            try {
                await pollForMessages(lastMessageId, peerConnection);
            } finally {
                if (!cancelled) {
                    timeoutId = window.setTimeout(runPoll, POLLING_INTERVAL_MS);
                }
            }
        };

        timeoutId = window.setTimeout(runPoll, POLLING_INTERVAL_MS);

        return () => {
            cancelled = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [connectionStatus, sessionId, answerProcessed, lastMessageId, peerConnection]);

    // api omitted from deps: ApiProvider recreates api on token refresh, which would tear down and
    // reestablish the WebRTC connection unnecessarily; the axios interceptor handles token refresh for API calls.
    useEffect(() => {
        if (sessionId && api) {
            connect();
        }

        return () => {
            cleanup();
        };
    }, [sessionId]);

    useEffect(() => {
        for (let i = 0; i < MAX_VIDEO_FEEDS; i++) {
            const el = videoRefs.current[i];
            const slot = videoFeeds[i];
            if (el) {
                el.srcObject = slot?.stream ?? null;
                if (slot?.stream) {
                    el.play().catch((err) => console.error('Error playing video:', err));
                }
            }
        }
    }, [videoFeeds]);

    useEffect(() => {
        if (dataChannel) {
            dataChannel.onclose = () => {
                setDataChannel(null);
            };

            dataChannel.onerror = () => {
                console.error('Data channel error');
            };

            dataChannel.onmessage = (ev) => {
                const text = dataChannelPayloadToString(ev.data);
                handleDataChannelMessage(text, setTrackNameByMid);
            };
        }
    }, [dataChannel]);

    const cleanup = () => {
        setConnectionStatus('connection_ended');
        setClosedGracefully(true);
        setPeerConnection((prev) => {
            if (prev) {
                prev.close();
            }
            return null;
        });
        setDataChannel((prev) => {
            if (prev) {
                prev.close();
            }
            return null;
        });
        for (let i = 0; i < MAX_VIDEO_FEEDS; i++) {
            const el = videoRefs.current[i];
            if (el) {
                el.srcObject = null;
            }
        }
        setTrackNameByMid({});
        setVideoFeeds([]);
        setErrorMessage(null);
        setLastMessageId(-1);
        pendingIceCandidatesRef.current = [];
    };

    const sendOffer = async (sdp: string) => {
        if (!sessionId) {
            return;
        }

        api.apiV0BrowserStreamSessionIdOfferPost({
            sessionId: sessionId,
            browserStreamOfferRequest: {
                sdp: sdp,
            },
        }).catch((error) => {
            setConnectionStatus('disconnected');
            setErrorMessage(
                t(`${LIVE_STREAM_NS}.error_sending_offer`, {
                    error: getErrorMessage(error) || t(`${LIVE_STREAM_NS}.unknown_error`),
                }),
            );
            throw error;
        });
    };

    const sendIceCandidate = async (candidate: RTCIceCandidate) => {
        if (!sessionId) return;

        api.apiV0BrowserStreamSessionIdIcePost({
            sessionId: sessionId,
            browserStreamIceRequest: {
                candidate: candidate.candidate || '',
                sdpMid: candidate.sdpMid || '',
                sdpMLineIndex: candidate.sdpMLineIndex || 0,
            },
        }).catch((error) => {
            console.error('Error sending ICE candidate:', error);
        });
    };

    const processMessage = async (message: BrowserStreamMessagesResponseMessages, pc: RTCPeerConnection | null) => {
        switch (message.type) {
            case BrowserStreamMessagesResponseMessagesTypeEnum.Answer:
                setAnswerProcessed(false);
                handleAnswer(message, pc).then(() => setAnswerProcessed(true));
                break;
            case BrowserStreamMessagesResponseMessagesTypeEnum.Ice:
                if (pc?.remoteDescription) {
                    await handleIceCandidate(message, pc);
                } else {
                    pendingIceCandidatesRef.current.push(message);
                }
                break;
            case BrowserStreamMessagesResponseMessagesTypeEnum.Terminate:
                console.log('Received terminate, stopping session');
                cleanup();
                break;
            default:
                // offer is sent by browser; other unknown types ignored
                break;
        }
    };

    const pollForMessages = async (afterId: number, pc: RTCPeerConnection | null) => {
        if (!sessionId) {
            return;
        }

        await api
            .apiV0BrowserStreamSessionIdMessagesGet({
                sessionId: sessionId,
                after: afterId,
            })
            .then(async (response) => {
                const data = response.data;
                const messages = data.messages || [];

                let maxId = afterId;
                for (let i = 0; i < messages.length; i++) {
                    const message = messages[i];
                    await processMessage(message, pc);
                    if (message.id !== undefined && message.id > maxId) {
                        maxId = message.id;
                    }
                }
                setLastMessageId(maxId);
            })
            .catch((error) => {
                console.error('Error polling for messages:', error);

                const errorWithResponse = error as { response?: { status?: number } };
                const errorStatus = errorWithResponse?.response?.status;
                if (errorStatus === 404) {
                    setConnectionStatus('disconnected');
                    setErrorMessage(t(`${LIVE_STREAM_NS}.session_not_found`));
                    return;
                }
                if (errorStatus === 401 || errorStatus === 403) {
                    setConnectionStatus('disconnected');
                    setErrorMessage(t(`${LIVE_STREAM_NS}.auth_failed_refresh`));
                    return;
                }
            });
    };

    const initializePeerConnection = (): RTCPeerConnection => {
        const servers = iceServers && iceServers.length > 0 ? iceServers : [{ urls: 'stun:stun.l.google.com:19302' }];
        const config = {
            iceServers: servers,
            iceCandidatePoolSize: 0,
        };

        const pc = new RTCPeerConnection(config);

        pc.ontrack = (event) => {
            if (event.track.kind === 'video') {
                const mid = event.transceiver?.mid ?? null;
                const stream = event.streams[0] || new MediaStream([event.track]);
                setVideoFeeds((prev) => {
                    const index = prev.length;
                    const trackId = mid ?? `VIDEO_TRACK_${index}`;
                    event.track.onended = () => {
                        setVideoFeeds((prev) => prev.filter((f) => f.trackId !== trackId));
                    };
                    return [...prev, { stream, trackId }];
                });
            }
        };

        // Use only the data channel created by the device (received in ondatachannel with the answer).
        pc.ondatachannel = (event) => {
            const channel = event.channel;
            setDataChannel(channel);
        };

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                sendIceCandidate(event.candidate);
            }
        };

        return pc;
    };

    const createAndSendOffer = async (pc: RTCPeerConnection) => {
        if (!sessionId) return;

        try {
            pc.addTransceiver('audio', { direction: 'recvonly' });
            for (let i = 0; i < MAX_VIDEO_FEEDS; i++) {
                pc.addTransceiver('video', { direction: 'recvonly' });
            }

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            const offerSdp = pc.localDescription?.sdp || '';
            await sendOffer(offerSdp);
        } catch (error) {
            setConnectionStatus('disconnected');
            setErrorMessage(
                t(`${LIVE_STREAM_NS}.error_creating_session`, {
                    error: getErrorMessage(error) || t(`${LIVE_STREAM_NS}.unknown_error`),
                }),
            );
            throw error;
        }
    };

    const handleAnswer = async (message: BrowserStreamMessagesResponseMessages, pc: RTCPeerConnection | null) => {
        if (!pc) return;

        if (!message.sdp) {
            throw new Error('Answer message missing SDP');
        }

        try {
            const parsed = parseTrackNamesFromSdp(message.sdp);
            setTrackNameByMid(parsed);

            await pc.setRemoteDescription(
                new RTCSessionDescription({
                    type: 'answer',
                    sdp: message.sdp,
                }),
            );
            const pending = pendingIceCandidatesRef.current;
            pendingIceCandidatesRef.current = [];
            for (const iceMessage of pending) {
                await handleIceCandidate(iceMessage, pc);
            }
        } catch (error) {
            setConnectionStatus('disconnected');
            setErrorMessage(
                t(`${LIVE_STREAM_NS}.error_setting_up_stream`, {
                    error: getErrorMessage(error) || t(`${LIVE_STREAM_NS}.unknown_error`),
                }),
            );
        }
    };

    const handleIceCandidate = async (message: BrowserStreamMessagesResponseMessages, pc: RTCPeerConnection | null) => {
        if (!pc) return;

        try {
            await pc.addIceCandidate(
                new RTCIceCandidate({
                    candidate: message.candidate,
                    sdpMid: message.sdpMid,
                    sdpMLineIndex: message.sdpMLineIndex,
                }),
            );
        } catch (error) {
            console.error('Error adding ICE candidate:', error);
        }
    };

    const connect = async () => {
        if (!sessionId || !api) {
            setConnectionStatus('disconnected');
            setErrorMessage(t(`${LIVE_STREAM_NS}.missing_session_or_auth`));
            return;
        }

        setConnectionStatus('connecting');
        setErrorMessage(null);
        setClosedGracefully(false);

        try {
            const pc = initializePeerConnection();
            setPeerConnection(pc);
            await createAndSendOffer(pc);
        } catch (error) {
            setConnectionStatus('disconnected');
            setErrorMessage(
                t(`${LIVE_STREAM_NS}.error_creating_session`, {
                    error: getErrorMessage(error) || t(`${LIVE_STREAM_NS}.unknown_error`),
                }),
            );
        }
    };

    const handleCloseRequest = () => {
        if (['connected', 'reconnecting', 'connecting'].includes(connectionStatus)) {
            setShowExitConfirm(true);
        } else {
            onClose();
        }
    };
    const handleConfirmExit = () => {
        setShowExitConfirm(false);
        onClose();
    };
    const handleCancelExit = () => setShowExitConfirm(false);

    if (sessionId === undefined) return null;

    // Only show feeds that received a track from the device; unsupported
    // slots (no track ever received) are never displayed.
    const activeFeeds = videoFeeds
        .map((feed, index) => (feed === null ? null : { ...feed, index }))
        .filter((x): x is VideoFeedSlot & { index: number } => x !== null);
    const primaryFeed = activeFeeds.find((f) => f.trackId === primaryTrackId);
    const orderedFeeds =
        primaryFeed !== undefined
            ? [primaryFeed, ...activeFeeds.filter((f) => f.trackId !== primaryTrackId)]
            : activeFeeds;
    const gridDef = STREAMS_GRID_DEFS['1x1+3x1'];
    const gridAreas = ['main', 'secondary0', 'secondary1', 'secondary2'] as const;

    return (
        <>
            <Dialog
                id="live-stream-modal"
                open={true}
                onClose={handleCloseRequest}
                fullWidth
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: '90%',
                        height: '90%',
                        maxWidth: 'none',
                        maxHeight: '90%',
                        backgroundColor: 'bgColor.main',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <LiveStreamModalHeader
                    onClose={handleCloseRequest}
                    title={t(`${LIVE_STREAM_NS}.label`)}
                    vin={vin}
                    connectionStatus={connectionStatus}
                    statusText={getStatusDisplayText(connectionStatus, errorMessage)}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        backgroundColor: 'bgColor.main',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            flex: 1,
                            minHeight: 0,
                            marginBottom: 1,
                            borderRadius: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {activeFeeds.length > 0 ? (
                            <Box
                                id="live-stream-video-grid"
                                sx={{
                                    display: 'grid',
                                    width: '95%',
                                    ...gridDef.grid,
                                    gap: 0.5,
                                    borderRadius: 1,
                                }}
                            >
                                {orderedFeeds.map(({ trackId, index }, i) => (
                                    <Box
                                        key={trackId}
                                        id={`live-stream-video-feed-${trackId}`}
                                        sx={{ gridArea: gridAreas[i], minHeight: 0, height: '100%', display: 'flex' }}
                                    >
                                        <LiveStreamVideoFeed
                                            displayName={getCameraDisplayName(trackNameByMid[trackId] ?? trackId)}
                                            isPrimary={i === 0}
                                            onSelect={() => setPrimaryTrackId(trackId)}
                                            setVideoRef={(el) => {
                                                videoRefs.current[index] = el;
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        ) : connectionStatus === 'connecting' ? (
                            <CircularProgress size="10vh" aria-label={t(`${LIVE_STREAM_NS}.status_connecting`)} />
                        ) : null}
                    </Box>
                    {!sessionId && (
                        <Typography sx={{ color: 'text.primary', marginTop: 2 }}>
                            {t(`${LIVE_STREAM_NS}.waiting_for_session`)}
                        </Typography>
                    )}
                </Box>
            </Dialog>
            <LiveStreamExitConfirmDialog
                open={showExitConfirm}
                onCancel={handleCancelExit}
                onConfirm={handleConfirmExit}
            />
        </>
    );
};

export default LiveStreamModal;
